import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import screenfull from 'screenfull';
import classNames from 'classnames';
import { Children as ChildrenPropTypes } from '@instructure/ui-prop-types';
import { pickProps } from '@instructure/ui-react-utils';
import themeable from '@instructure/ui-themeable';
import uid from '@instructure/uid';
import PlayerContext from './PlayerContext';
import PlayerControls from '../PlayerControls';
import PlayerMarker from '../PlayerMarker';
import PlayerOverlay from '../PlayerOverlay';
import PlayerPlayhead from '../PlayerPlayhead';
import { Captions } from './Captions';
import PlayerPropTypes, { playerEventsTypeSet, rendererEventsTypeSet } from './PropTypes';
import { applyTranslations, translate, TranslationPropTypes } from '../../constants/translated/translations';
import Inert from './Inert';
import MediaContext from './MediaContext';
import Loading from '../Loading';
import { PAUSED, PLAYING, ENDED, WINDOWED_SCREEN, FULL_SCREEN } from '../../constants';
var styles = {
  componentId: 'cGOLc',
  template: function template(theme) {
    return "\n\n.cGOLc_fWpV{height:100%;width:100%}\n\n.cGOLc_fWpV:-webkit-full-screen video{height:100%}\n\n.cGOLc_fWpV:fullscreen video{height:100%}\n\n.cGOLc_fWpV:-ms-fullscreen video{height:100%}\n\n.cGOLc_eiKY{height:auto;max-height:inherit;max-width:inherit;min-height:inherit;min-width:inherit;width:auto}\n\n.cGOLc_cvfe{width:100%}\n\n.cGOLc_csZL{height:100%}\n\n.cGOLc_cORI{align-items:center;background-color:#000;display:flex;height:100%;justify-content:center;max-height:inherit;max-width:inherit;min-height:inherit;min-width:inherit;overflow:hidden;position:relative;transition:height 0.1s ease-in-out}\n\n.cGOLc_cORI:focus{box-shadow:0 0 0 ".concat(theme.focusOutlineWeight || 'inherit', " ").concat(theme.focusOutlineColor || 'inherit', ";outline:none}\n\n.cGOLc_ejHP{align-items:center;display:flex;height:100%;justify-content:center;max-height:inherit;max-width:inherit;min-height:inherit;min-width:inherit;width:100%}");
  },
  'fullScreenContainer': 'cGOLc_fWpV',
  'fullScreenContainerWindow': 'cGOLc_eiKY',
  'fluidWidth': 'cGOLc_cvfe',
  'fluidHeight': 'cGOLc_csZL',
  'mediaPlayerContainer': 'cGOLc_cORI',
  'playerRendererContainer': 'cGOLc_ejHP'
};
import theme from './theme';
export var SEEK_INTERVAL_SECONDS = 5;
export var JUMP_INTERVAL_SECONDS = 30;
export var SEEK_VOLUME_INTERVAL = 0.05;
export var JUMP_VOLUME_INTERVAL = 0.1;
/**
---
category: components
experimental: true
---
**/

var Player = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Player, _Component);

  function Player(props) {
    var _this;

    _classCallCheck(this, Player);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Player).call(this, props));
    _this.fullScreenContainer = null;
    _this.videoContainer = null;
    _this.video = null;
    _this.state = {
      seeking: false,
      loadingOverlay: false,
      // false | 'passthrough' | 'backdrop'
      controlActivated: false,
      controlHovered: false,
      focusEnabled: true,
      mediaState: PAUSED,
      screenState: WINDOWED_SCREEN,
      muted: false,
      volume: 1,
      playbackSpeed: 1,
      selectedSrc: _this.getSourceFromProps(),
      selectedTrackId: 0,
      // TODO: change `0` to `null` once INSTUI-2429 is resolved
      sources: _this.props.sources,
      tracks: _this.getPossiblyModifiedTracks(_this.props.tracks),
      showControls: true,
      videoId: uid('Player')
    };

    _this.handleSeeking = function () {
      _this.setState({
        seeking: true
      });
    };

    _this.handleSeeked = function () {
      _this.setState({
        seeking: false
      });
    };

    _this.hasBeenWarnedOnce = false;

    _this.handleContextMenu = function (e) {
      if (_this.props.disableRightClick && e.target.closest(".".concat(styles.playerRendererContainer))) {
        e.preventDefault();
      }
    };

    _this.handleKeyPress = function (actions) {
      return function (e) {
        var _this$state = _this.state,
            currentTime = _this$state.currentTime,
            duration = _this$state.duration;

        var playAction = function playAction() {
          if (_this.videoContainer === e.target) {
            actions.togglePlay();
            return true;
          }

          return false;
        };

        var seekAction = function seekAction(time) {
          if (!_this.state.controlActivated) {
            actions.seek(time);
            return true;
          }

          return false;
        };

        var keyHandlers = {
          ArrowLeft: function ArrowLeft() {
            return seekAction(currentTime - SEEK_INTERVAL_SECONDS);
          },
          ArrowRight: function ArrowRight() {
            return seekAction(currentTime + SEEK_INTERVAL_SECONDS);
          },
          ArrowUp: function ArrowUp() {
            return seekAction(currentTime + SEEK_INTERVAL_SECONDS);
          },
          ArrowDown: function ArrowDown() {
            return seekAction(currentTime - SEEK_INTERVAL_SECONDS);
          },
          PageUp: function PageUp() {
            actions.seek(currentTime + JUMP_INTERVAL_SECONDS);
            return true;
          },
          PageDown: function PageDown() {
            actions.seek(currentTime - JUMP_INTERVAL_SECONDS);
            return true;
          },
          Home: function Home() {
            actions.seek(0);
            return true;
          },
          End: function End() {
            actions.seek(duration);
            return true;
          },
          ' ': playAction,
          Enter: playAction,

          /*
            Bug specific to IE 11 for fullscreen keyboard shortcut.
            onKeyDown on neither 'f' nor 'F' work, but clicking the
            FullScreenButton works. I've also tried getting the
            FullScreenButton's ref and invoking click() (HTML API).
          */
          f: function f() {
            actions.toggleFullScreen();
            return true;
          },
          F: function F() {
            actions.toggleFullScreen();
            return true;
          },
          m: function m() {
            actions.toggleMute();
            return true;
          },
          M: function M() {
            actions.toggleMute();
            return true;
          }
        };

        if (e.key in keyHandlers && keyHandlers[e.key]()) {
          e.preventDefault();
          actions.showControls();
        }
      };
    };

    _this.setFullScreen = function (newState) {
      if (screenfull && screenfull.enabled) {
        if (newState && _this.state.screenState === WINDOWED_SCREEN) {
          screenfull.request(_this.fullScreenContainer);
        } else if (!newState && _this.state.screenState === FULL_SCREEN) {
          screenfull.exit();
        }
      }
    };

    _this.toggleFullScreen = function () {
      if (screenfull && screenfull.enabled) {
        screenfull.toggle(_this.fullScreenContainer);
      }
    };

    _this.updateScreenState = function () {
      if (!_this.fullScreenContainer) {
        return;
      }

      var screenState = screenfull && screenfull.isFullscreen ? FULL_SCREEN : WINDOWED_SCREEN;

      _this.setState({
        screenState: screenState
      });
    };

    _this.setVideoContainerRef = function (el) {
      if (_this.videoContainer === null) {
        _this.videoContainer = el;
      }
    };

    _this.setFullScreenContainerRef = function (el) {
      if (_this.fullScreenContainer === null) {
        _this.fullScreenContainer = el;
      }
    };

    _this.activateControl = function () {
      _this.setState({
        controlActivated: true
      });
    };

    _this.deactivateControl = function () {
      _this.setState({
        controlActivated: false
      });
    };

    _this.setControlHovered = function (newValue) {
      _this.setState({
        controlHovered: newValue
      });
    };

    _this.setFocusEnabled = function (newValue) {
      _this.setState({
        focusEnabled: newValue
      });
    };

    _this.showControls = function () {
      var hideControlsTimeout = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 2500;

      if (_this.props.alwaysShowControls) {
        return;
      }

      if (_this._hideControlsTimeoutId) {
        clearTimeout(_this._hideControlsTimeoutId);
      }

      var _nextTimeout = function _nextTimeout() {
        _this._hideControlsTimeoutId = setTimeout(function () {
          if (_this.state.controlActivated || _this.state.controlHovered) {
            return _nextTimeout();
          }

          if (_this.state.mediaState === PLAYING) {
            _this.setState({
              showControls: false
            });

            _this.props.onControlsHidden();
          }
        }, hideControlsTimeout);
      };

      var prevValue = _this.state.showControls;

      _this.setState({
        showControls: true
      }, _nextTimeout);

      if (!prevValue) {
        _this.props.onControlsShown();
      }
    };

    _this.onRendererStateChange = function (rendererStateUpdater) {
      var callback = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : function () {};

      _this.setState(rendererStateUpdater, callback);
    };

    _this.setActions = function (actions, _ref) {
      var setAction = _ref.setAction;
      Object.entries(actions).forEach(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 2),
            fnName = _ref3[0],
            fn = _ref3[1];

        setAction(fnName, fn);
      });
    };

    _this.onTrackChange = function (selectedTrackId) {
      _this.setState({
        selectedTrackId: selectedTrackId
      });
    };

    _this.setLoadingOverlay = function (loadingOverlay) {
      _this.setState({
        loadingOverlay: loadingOverlay
      });
    };

    applyTranslations(_this.props.translations);
    return _this;
  }

  _createClass(Player, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      screenfull && screenfull.on('change', this.updateScreenState);
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps, prevState) {
      if (prevState.mediaState !== this.state.mediaState && this.state.mediaState === ENDED) {
        this.showControls();
      }

      var tracks = this.props.tracks;

      if (prevProps.tracks !== tracks) {
        this.setState({
          tracks: this.getPossiblyModifiedTracks(tracks)
        });
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      screenfull && screenfull.off('change', this.updateScreenState); // remove the video ref and stop applying video props

      this.videoContainer = null;
      this.fullScreenContainer = null;
    }
  }, {
    key: "getPossiblyModifiedTracks",
    value: function getPossiblyModifiedTracks(tracks) {
      var _this2 = this;

      return tracks.map(function (track) {
        var result = _objectSpread({}, track);

        if (!result.id) {
          _this2.warnOnce();

          result.id = result.label;
        }

        return result;
      });
    }
  }, {
    key: "warnOnce",
    value: function warnOnce() {
      if (this.hasBeenWarnedOnce) {
        return;
      }

      console.warn('[ui-media-player] track.label will no longer be used to uniquely identify track in 8.0 in order to support duplicate caption labels. Please provide an id for each track (track.id).');
      this.hasBeenWarnedOnce = true;
    }
  }, {
    key: "getSourceFromProps",
    value: function getSourceFromProps() {
      var sources = this.props.sources;

      if (typeof sources === 'string') {
        return sources;
      }

      if (sources.length === 0) {
        return null;
      }

      if (typeof sources[0] === 'string') {
        return sources[0];
      }

      for (var i = 0; i < sources.length; i++) {
        if (sources[i].defaultSelected) {
          return sources[i].src;
        }
      }

      return sources[0].src;
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var _this$props = this.props,
          Renderer = _this$props.renderer,
          controls = _this$props.controls,
          fluidWidth = _this$props.fluidWidth,
          fluidHeight = _this$props.fluidHeight,
          actionsRef = _this$props.actionsRef;
      var _this$state2 = this.state,
          tracks = _this$state2.tracks,
          focusEnabled = _this$state2.focusEnabled,
          screenState = _this$state2.screenState,
          loadingOverlay = _this$state2.loadingOverlay;
      var constants = {
        SEEK_INTERVAL_SECONDS: SEEK_INTERVAL_SECONDS,
        JUMP_INTERVAL_SECONDS: JUMP_INTERVAL_SECONDS,
        SEEK_VOLUME_INTERVAL: SEEK_VOLUME_INTERVAL,
        JUMP_VOLUME_INTERVAL: JUMP_VOLUME_INTERVAL
      };
      return React.createElement(PlayerContext, null, function (playerContext) {
        var _classNames, _classNames2;

        var actionsSetter = function actionsSetter(actions) {
          _this3.setActions(actions, playerContext);

          actionsRef && actionsRef(playerContext.actions);
        };

        actionsSetter({
          activateControl: _this3.activateControl,
          deactivateControl: _this3.deactivateControl,
          setFullScreen: _this3.setFullScreen,
          toggleFullScreen: _this3.toggleFullScreen,
          setControlHovered: _this3.setControlHovered,
          setFocusEnabled: _this3.setFocusEnabled,
          showControls: _this3.showControls
        });
        var mediaContextValue = {
          state: _this3.state,
          fullScreenContainerRef: function fullScreenContainerRef() {
            return _this3.fullScreenContainer;
          },
          actions: playerContext.actions,
          setActions: actionsSetter,
          playbackSpeedOptions: playerContext.playbackSpeedOptions,
          constants: constants
        };
        var fullScreenContainerProps = {
          className: classNames((_classNames = {}, _defineProperty(_classNames, styles.fullScreenContainer, true), _defineProperty(_classNames, styles.fullScreenContainerWindow, screenState === WINDOWED_SCREEN), _defineProperty(_classNames, styles.fluidWidth, fluidWidth), _defineProperty(_classNames, styles.fluidHeight, fluidHeight), _classNames)),
          ref: _this3.setFullScreenContainerRef
        };
        var mediaPlayerContainerProps = {
          className: classNames((_classNames2 = {}, _defineProperty(_classNames2, styles.mediaPlayerContainer, true), _defineProperty(_classNames2, styles.fluidWidth, fluidWidth), _defineProperty(_classNames2, styles.fluidHeight, fluidHeight), _classNames2)),
          onContextMenu: _this3.handleContextMenu,
          onKeyDown: _this3.handleKeyPress(playerContext.actions),
          onFocus: function onFocus() {
            return _this3.showControls();
          },
          onMouseMove: function onMouseMove() {
            return _this3.showControls();
          },
          onClick: playerContext.actions.togglePlay,
          tabIndex: focusEnabled ? 0 : -1,
          role: 'presentation',
          'aria-label': _this3.props.label || translate('ARIA_VIDEO_LABEL'),
          ref: _this3.setVideoContainerRef
        };
        var defaultProps = {
          onSeeking: _this3.handleSeeking,
          onSeeked: _this3.handleSeeked
        };
        var rendererEventsTypeSetFromProps = pickProps(_this3.props, rendererEventsTypeSet);
        var eventProps = {};
        var allEvents = [].concat(_toConsumableArray(Object.keys(rendererEventsTypeSetFromProps)), _toConsumableArray(Object.keys(defaultProps)));
        allEvents.forEach(function (key) {
          var propFn = rendererEventsTypeSetFromProps[key];
          var defaultPropFn = defaultProps[key];

          eventProps[key] = function (event, error) {
            if (propFn) {
              propFn(event, mediaContextValue.state, error);
            }

            if (defaultPropFn) {
              defaultPropFn(event);
            }
          };
        });
        return React.createElement("div", fullScreenContainerProps, React.createElement("div", mediaPlayerContainerProps, React.createElement("div", {
          className: classNames(styles.playerRendererContainer)
        }, React.createElement(Renderer, Object.assign({
          poster: _this3.props.poster,
          videoId: _this3.state.videoId,
          selectedSrc: _this3.state.selectedSrc,
          setActions: actionsSetter,
          onRendererStateChange: _this3.onRendererStateChange,
          mediaState: _this3.state.mediaState,
          setPlaybackSpeedOptions: playerContext.setPlaybackSpeedOptions,
          setLoadingOverlay: _this3.setLoadingOverlay
        }, eventProps)), React.createElement(Captions, {
          tracks: tracks,
          onTrackChange: _this3.onTrackChange,
          setActions: actionsSetter,
          currentTime: _this3.state.currentTime || 0,
          captionPosition: _this3.props.captionPosition,
          captionOffset: _this3.props.captionOffset,
          showControls: _this3.state.showControls
        })), React.createElement(MediaContext.Provider, {
          value: mediaContextValue
        }, React.createElement("div", null, _this3.props.children, React.createElement(Inert, {
          active: !focusEnabled
        }, controls(PlayerControls)), React.createElement(LoadingOverlay, {
          open: loadingOverlay,
          passthrough: loadingOverlay === 'passthrough',
          backdrop: loadingOverlay === 'backdrop'
        })))));
      });
    }
  }]);

  Player.displayName = "Player";
  return Player;
}(Component), _class2.propTypes = _objectSpread({
  renderer: PropTypes.func.isRequired,

  /**
   * URL(s) of video to play
   */
  sources: PlayerPropTypes.sourcesType,

  /**
   * tracks of the video to play
   */
  tracks: PlayerPropTypes.tracksType,

  /**
   * Function invoked on every render with state and actions.
   * Use this to provide a custom set of video controls.
   * Default player controls will be provided if undefined.
   */
  controls: PropTypes.func,

  /**
   * If set to true, the controls will never dismiss.
   */
  alwaysShowControls: PropTypes.bool,

  /**
   * Give the player a label to be read by screen readers.
   */
  label: PropTypes.string,

  /**
   * The poster image to use before the media is played.
   */
  poster: PropTypes.string,

  /**
   * Label overrides for i18n. Defaults to english
   * See src/constants/translated/translations.js for default values
   */
  translations: PropTypes.shape(TranslationPropTypes),

  /**
   * Children of the <Player />
   */
  children: ChildrenPropTypes.oneOf([PlayerOverlay]),

  /**
   * indicates the player should fill the width of its container
   */
  fluidWidth: PropTypes.bool,

  /**
   * indicates the player should fill the height of its container
   */
  fluidHeight: PropTypes.bool,

  /**
   * Reference to actions object
   */
  actionsRef: PropTypes.func,

  /**
   * Disable right click on the player container
   */
  disableRightClick: PropTypes.bool,
  captionPosition: Captions.propTypes.captionPosition,
  captionOffset: Captions.propTypes.captionOffset
}, playerEventsTypeSet, {}, rendererEventsTypeSet), _class2.defaultProps = {
  tracks: [],
  controls: function controls(PlayerControls) {
    return React.createElement(PlayerControls, null, React.createElement(PlayerControls.PlayPauseButton, {
      position: "start"
    }), React.createElement(PlayerControls.Timebar, null), React.createElement(PlayerControls.Volume, null), React.createElement(PlayerControls.PlaybackSpeed, null), React.createElement(PlayerControls.TrackChooser, null), React.createElement(PlayerControls.SourceChooser, null), React.createElement(PlayerControls.FullScreenButton, {
      position: "end"
    }));
  },
  actionsRef: null,
  alwaysShowControls: false,
  children: null,
  poster: null,
  label: '',
  translations: {},
  fluidWidth: true,
  fluidHeight: false,
  disableRightClick: false,
  onControlsHidden: function onControlsHidden() {},
  onControlsShown: function onControlsShown() {},
  captionPosition: Captions.defaultProps.captionPosition,
  captionOffset: Captions.defaultProps.captionOffset
}, _class2.Marker = PlayerMarker, _class2.Overlay = function (props) {
  return React.createElement(MediaContext.Consumer, null, function (_ref4) {
    var actions = _ref4.actions;
    return React.createElement(PlayerOverlay, Object.assign({
      setFocusEnabled: actions.setFocusEnabled
    }, props));
  });
}, _class2.Playhead = PlayerPlayhead, _class2.Control = PlayerControls.Control, _temp)) || _class);

var _ref6 = /*#__PURE__*/React.createElement(Loading, null);

var LoadingOverlay = function LoadingOverlay(_ref5) {
  var open = _ref5.open,
      props = _objectWithoutProperties(_ref5, ["open"]);

  return open && React.createElement(Player.Overlay, props, function () {
    return _ref6;
  });
};

export default Player;
export { Player, PlayerPropTypes };